import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Mail, Phone, MapPin, Building2, Minus, Plus, CheckCircle, ExternalLink } from 'lucide-react';
import { Product } from '../types';
import { BRANCHES } from '../config/siteConfig';
import { WhatsAppMessageService } from '../utils/whatsappMessageService';
import { useToast } from '../context/ToastContext';
import { WhatsAppIcon } from './BrandLogos';

interface ShippingItem {
    product: Product;
    quantity: number;
    variantText?: string;
}

interface ShippingFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    items: ShippingItem[];
    onSuccess?: () => void;
}

const ShippingFormModal: React.FC<ShippingFormModalProps> = ({ isOpen, onClose, items, onSuccess }) => {
    const { showToast } = useToast();
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });
    
    // Architect: Use local state for items to allow quantity adjustments within the modal
    const [localItems, setLocalItems] = useState<ShippingItem[]>([]);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [whatsappUrl, setWhatsappUrl] = useState<string>('');

    useEffect(() => {
        if (isOpen) {
            setLocalItems([...items]);
            setIsSubmitted(false);
            setWhatsappUrl('');
        }
    }, [isOpen, items]);
    
    const updateLocalQuantity = (index: number, newQuantity: number) => {
        if (newQuantity < 1) return;
        setLocalItems(prev => prev.map((item, i) => 
            i === index ? { ...item, quantity: newQuantity } : item
        ));
    };

    // Si hay múltiples teléfonos/sucursales, necesitamos un estado para seleccionar uno
    const availablePhones = BRANCHES.flatMap(branch => 
        branch.phones.map(phone => ({
            branchName: branch.name,
            phone: phone
        }))
    );
    
    const [selectedPhoneIndex, setSelectedPhoneIndex] = useState<number>(0);

    // Block body scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.phone || !formData.address) {
            showToast('Por favor, completa todos los campos requeridos.', 'error');
            return;
        }

        if (localItems.length === 0) {
            showToast('No hay productos para solicitar.', 'error');
            return;
        }

        const selectedPhone = availablePhones[selectedPhoneIndex].phone;
        const branchName = availablePhones[selectedPhoneIndex].branchName;
        
        const message = WhatsAppMessageService.buildShippingRequestMessage(formData, localItems);
        const formattedPhone = selectedPhone.replace(/[^0-9]/g, '');
        const encodedMessage = encodeURIComponent(message);
        const url = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;

        setWhatsappUrl(url);
        setIsSubmitted(true);

        WhatsAppMessageService.trackWhatsAppClick({
            source: 'product_modal',
            branch: branchName,
            phoneIndex: 0,
            phoneNumber: selectedPhone,
            productName: localItems.length === 1 ? localItems[0].product.name : `Envío Múltiple (${localItems.length})`,
            messageType: 'buy'
        });
        
        // Intentar abrir la pestaña directamente
        window.open(url, '_blank');
        showToast('¡Información registrada! Te transferimos a WhatsApp.', 'success');
        
        if (onSuccess) {
            onSuccess();
        }
    };

    const modalContent = (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9998]"
            />
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4 pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl pointer-events-auto flex flex-col max-h-[90vh]"
                    onClick={(e) => e.stopPropagation()}
                >
                    {isSubmitted ? (
                        <div className="p-8 flex flex-col items-center text-center space-y-6">
                            <motion.div
                                initial={{ scale: 0, rotate: -45 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                                className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center text-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.3)]"
                            >
                                <CheckCircle className="w-10 h-10" />
                            </motion.div>

                            <div className="space-y-2">
                                <h3 className="text-2xl font-display font-black uppercase text-white tracking-wide">
                                    ¡Solicitud Lista para Enviar!
                                </h3>
                                <p className="text-zinc-400 text-sm max-w-sm mx-auto leading-relaxed">
                                    Hemos registrado tus datos para <span className="text-white font-medium">{formData.name}</span>. Te estamos transfiriendo a WhatsApp para coordinar la entrega con Guzmán.
                                </p>
                            </div>

                            <div className="w-full bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 text-left text-xs space-y-2 text-zinc-300">
                                <div className="flex justify-between border-b border-zinc-800/80 pb-2">
                                    <span className="text-zinc-500">Destinatario:</span>
                                    <span className="font-semibold text-white">{formData.name} ({formData.phone})</span>
                                </div>
                                <div className="flex justify-between border-b border-zinc-800/80 pb-2">
                                    <span className="text-zinc-500">Dirección:</span>
                                    <span className="font-semibold text-white truncate max-w-[200px]">{formData.address}</span>
                                </div>
                                <div className="flex justify-between pt-1">
                                    <span className="text-zinc-500">Artículos:</span>
                                    <span className="font-semibold text-emerald-400">{localItems.reduce((acc, i) => acc + i.quantity, 0)} pieza(s)</span>
                                </div>
                            </div>

                            <div className="w-full space-y-3 pt-2">
                                <button
                                    onClick={() => window.open(whatsappUrl, '_blank')}
                                    className="w-full bg-[#25D366] hover:bg-[#20ba59] text-black font-display font-black uppercase tracking-widest py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all shadow-[0_0_25px_rgba(37,211,102,0.3)] hover:scale-[1.01] active:scale-[0.99]"
                                >
                                    <span>Abrir WhatsApp Ahora</span>
                                    <WhatsAppIcon className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={onClose}
                                    className="w-full bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white text-xs uppercase tracking-wider py-3 rounded-xl transition-colors border border-zinc-800"
                                >
                                    Cerrar y seguir navegando
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                    <div className="p-6 border-b border-zinc-900 flex items-center justify-between shrink-0">
                        <div>
                            <h3 className="text-white font-display font-black uppercase tracking-widest text-lg">Solicitar Envío</h3>
                            <p className="text-zinc-500 text-xs mt-1 uppercase tracking-wider">Completa tus datos para procesar el pedido</p>
                        </div>
                        <button onClick={onClose} className="p-2 bg-zinc-900/50 hover:bg-zinc-800 rounded-full transition-colors text-zinc-400 hover:text-white">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Form Body */}
                    <div className="p-6 overflow-y-auto space-y-6 flex-grow custom-scrollbar">
                        <form id="shipping-form" onSubmit={handleSubmit} className="space-y-5">
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase mb-2">
                                        <User className="h-3 w-3" /> Nombre Completo *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#1aaaff] transition-colors"
                                        placeholder="Ej. Juan Pérez"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase mb-2">
                                            <Mail className="h-3 w-3" /> Correo *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#1aaaff] transition-colors"
                                            placeholder="correo@ejemplo.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase mb-2">
                                            <Phone className="h-3 w-3" /> Teléfono *
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#1aaaff] transition-colors"
                                            placeholder="7777-8888"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase mb-2">
                                        <MapPin className="h-3 w-3" /> Dirección de Envío *
                                    </label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        required
                                        rows={3}
                                        className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#1aaaff] transition-colors resize-none"
                                        placeholder="Ingresa tu dirección completa con referencias..."
                                    />
                                </div>

                                {/* Selección de Sucursal / Teléfono de destino */}
                                {availablePhones.length > 1 && (
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-bold text-zinc-400 uppercase mb-2">
                                            <Building2 className="h-3 w-3" /> Enviar solicitud a:
                                        </label>
                                        <select
                                            value={selectedPhoneIndex}
                                            onChange={(e) => setSelectedPhoneIndex(Number(e.target.value))}
                                            className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#1aaaff] transition-colors appearance-none"
                                            style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                                        >
                                            {availablePhones.map((info, idx) => (
                                                <option key={idx} value={idx} className="bg-zinc-900 text-white">
                                                    {info.branchName} - {info.phone}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* Resumen de artículos implícitos */}
                            <div className="mt-6 pt-6 border-t border-zinc-800/50">
                                <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Resumen de Pedido ({localItems.length} {localItems.length === 1 ? 'artículo' : 'artículos'})</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {localItems.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-zinc-900/30 p-3 rounded-lg border border-zinc-800/50 group">
                                            <div className="flex-1 pr-4">
                                                <p className="text-xs text-white font-bold truncate">{item.product.name}</p>
                                                {item.variantText && <p className="text-[10px] text-zinc-400 mt-0.5">{item.variantText}</p>}
                                            </div>
                                            
                                            {/* Quantity Selector */}
                                            <div className="flex items-center bg-black/40 rounded-lg border border-zinc-800 p-1">
                                                <button
                                                    type="button"
                                                    onClick={() => updateLocalQuantity(idx, item.quantity - 1)}
                                                    className="p-1 text-zinc-500 hover:text-white transition-colors"
                                                >
                                                    <Minus className="h-3 w-3" />
                                                </button>
                                                <span className="w-8 text-center text-white font-mono text-xs font-bold">{item.quantity}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => updateLocalQuantity(idx, item.quantity + 1)}
                                                    className="p-1 text-zinc-500 hover:text-white transition-colors"
                                                >
                                                    <Plus className="h-3 w-3" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </form>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-zinc-900 shrink-0 bg-zinc-950/80 rounded-b-2xl">
                        <button
                            type="submit"
                            form="shipping-form"
                            className="w-full bg-[#1aaaff] hover:bg-[#1aaaff]/90 text-white font-display font-black uppercase tracking-widest py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all hover:shadow-[0_0_20px_rgba(26,170,255,0.3)]"
                        >
                            <span>Enviar Solicitud a WhatsApp</span>
                            <WhatsAppIcon className="h-5 w-5" />
                        </button>
                    </div>
                        </>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );

    return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

export default ShippingFormModal;
